import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import type { GroupDto } from "@platform/core";
import {
  Alert,
  Button,
  DataGrid,
  Dialog,
  EmptyState,
  Field,
  Input,
  Textarea,
  useToast,
  type GridColDef,
} from "@platform/ui";
import { isApiError } from "../../api/errors";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { SLUG_PATTERN, SLUG_RE, useGroups } from "../../lib/access";
import { errorText } from "../../lib/errors";
import { formatDate } from "../../lib/format";
import { useGridText } from "../../lib/grid";
import { ErrorBanner, PageCard, ReloadButton } from "../../layout/Page";

export function CreateGroupDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: (g: GroupDto) => void;
}) {
  const { t } = useT();
  const api = useApi();
  const toast = useToast();
  const qc = useQueryClient();
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [touched, setTouched] = useState(false);
  const create = useMutation({
    mutationFn: () =>
      api.groups.create({
        id,
        name: name.trim() || id,
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: (g) => {
      toast({ title: t("groups.created"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["groups"] });
      setId("");
      setName("");
      setDescription("");
      setTouched(false);
      onOpenChange(false);
      onCreated(g);
    },
  });
  const fe = isApiError(create.error) ? create.error.fieldErrors() : {};
  const idInvalid = touched && !SLUG_RE.test(id);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("groups.createTitle")}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="create-group-form"
            data-testid="submit-group-button"
            loading={create.isPending}
            disabled={!SLUG_RE.test(id)}
          >
            {t("common.create")}
          </Button>
        </>
      }
    >
      <form
        id="create-group-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (SLUG_RE.test(id)) create.mutate();
        }}
      >
        {create.isError && (
          <Alert tone="error" className="mb-4">
            {errorText(create.error)}
          </Alert>
        )}
        <Field
          label={t("groups.id")}
          hint={t("groups.idHint")}
          required
          error={fe.id ?? (idInvalid ? t("access.slugInvalid") : undefined)}
        >
          {(p) => (
            <Input
              {...p}
              value={id}
              autoComplete="off"
              pattern={SLUG_PATTERN}
              placeholder="chat-agents"
              onBlur={() => setTouched(true)}
              onChange={(e) => setId(e.target.value.toLowerCase())}
            />
          )}
        </Field>
        <Field label={t("groups.name")} error={fe.name}>
          {(p) => <Input {...p} value={name} placeholder={id} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t("groups.description")} error={fe.description}>
          {(p) => (
            <Textarea
              {...p}
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}

export function GroupsPage() {
  const { t, locale } = useT();
  const { can, config } = useConsole();
  const navigate = useNavigate();
  const groups = useGroups();
  const [creating, setCreating] = useState(false);
  const text = useGridText();
  const open = (groupId: string) => void navigate({ to: "/groups/$groupId", params: { groupId } });
  const columns = useMemo<GridColDef<GroupDto>[]>(
    () => [
      {
        field: "name",
        headerName: t("groups.name"),
        minWidth: 220,
        hideable: false,
        renderCell: ({ row: g }) => (
          <div className="min-w-0">
            <p className="truncate font-medium">{g.name}</p>
            <p className="truncate font-mono text-xs text-ink-2">{g.id}</p>
          </div>
        ),
      },
      {
        field: "id",
        headerName: t("groups.id"),
        width: 180,
        hiddenByDefault: true,
        cellClassName: "font-mono text-xs",
      },
      { field: "description", headerName: t("groups.description"), minWidth: 240 },
      { field: "memberCount", headerName: t("groups.members"), type: "number", width: 120 },
      {
        field: "createdAt",
        headerName: t("users.col.created"),
        type: "dateTime",
        width: 140,
        valueFormatter: (v) => formatDate(String(v), locale, config.timeZone),
      },
    ],
    [t, locale, config.timeZone],
  );
  return (
    <PageCard>
      {groups.isError && <ErrorBanner error={groups.error} onRetry={() => void groups.refetch()} />}
      <p className="border-b border-line px-6 py-3 text-sm text-ink-2">{t("groups.intro")}</p>
      <DataGrid
        id="groups"
        label={t("nav.groups")}
        localeText={text}
        rows={groups.data?.items ?? []}
        columns={columns}
        loading={groups.isFetching}
        onRowClick={(g) => open(g.id)}
        getRowAttributes={(g) => ({ "data-testid": "group-row", "data-group": g.id })}
        noRowsContent={
          <EmptyState
            title={t("groups.empty")}
            action={
              can("groups:write") ? (
                <Button onClick={() => setCreating(true)}>{t("groups.add")}</Button>
              ) : undefined
            }
          />
        }
        toolbarActions={
          <>
            <ReloadButton onClick={() => void groups.refetch()} />
            {can("groups:write") && (
              <Button data-testid="add-group-button" onClick={() => setCreating(true)}>
                <Plus className="size-4" aria-hidden />
                {t("groups.add")}
              </Button>
            )}
          </>
        }
      />
      <CreateGroupDialog open={creating} onOpenChange={setCreating} onCreated={(g) => open(g.id)} />
    </PageCard>
  );
}
